package com.arena_web.data.dto

import kotlinx.serialization.Serializable

// ── Response DTOs ──

@Serializable
data class ChatDto(
    val id: String,
    val type: String,
    val name: String? = null,
    val imageUrl: String? = null,
    val privacy: String,
    val creatorId: String,
    val lastMessage: MessageDto? = null,
    val unreadCount: Long = 0,
    val isMuted: Boolean = false,
    val members: List<ChatMemberDto> = emptyList(),
    val createdAt: String,
    val updatedAt: String
)

@Serializable
data class ChatListItemDto(
    val id: String,
    val type: String,
    val name: String? = null,
    val imageUrl: String? = null,
    val privacy: String,
    val lastMessage: MessageDto? = null,
    val unreadCount: Long = 0,
    val isMuted: Boolean = false,
    val otherUserId: String? = null,
    val cardImageUrl: String? = null,
    val cardAvatarUrl: String? = null,
    val createdAt: String
)

@Serializable
data class ChatListPageDto(
    val items: List<ChatListItemDto>,
    val page: Int,
    val size: Int,
    val totalItems: Long,
    val totalPages: Int,
)

@Serializable
data class ChatMemberDto(
    val id: String,
    val userId: String,
    val username: String,
    val fullName: String? = null,
    val avatarUrl: String? = null,
    val overallRating: Double = 0.0,
    val position: String? = null,
    val club: String? = null,
    val country: String? = null,
    val cardImageUrl: String? = null,
    val cardAvatarUrl: String? = null,
    val role: String,
    val createdAt: String
)

@Serializable
data class MessageDto(
    val id: String,
    val chatId: String,
    val senderId: String,
    val senderUsername: String? = null,
    val senderAvatarUrl: String? = null,
    // Sender's main-card kit resolved server-side from the users/cards tables
    // — lets event chat render the card even after the sender was removed
    // from the event participant list.
    val senderCardAvatarUrl: String? = null,
    val senderCardAssetsBasePath: String? = null,
    val senderCardTextColor: String? = null,
    val content: String,
    val type: String,
    val isRead: Boolean,
    val fileUrl: String? = null,
    val fileName: String? = null,
    val fileSize: Long? = null,
    val mimeType: String? = null,
    val duration: Int? = null,
    val replyToId: String? = null,
    val replyToContent: String? = null,
    val replyToSenderName: String? = null,
    val isEdited: Boolean = false,
    val isPinned: Boolean = false,
    val forwardedFromChatId: String? = null,
    val forwardedFromMessageId: String? = null,
    val reactions: List<ReactionDto> = emptyList(),
    val createdAt: String
)

@Serializable
data class ReactionDto(
    val emoji: String,
    val count: Int,
    val userIds: List<String>
)

@Serializable
data class ChatInvitationDto(
    val id: String,
    val chatId: String,
    val chatName: String? = null,
    val inviterUsername: String? = null,
    val status: String,
    val createdAt: String
)

@Serializable
data class ChatJoinRequestDto(
    val id: String,
    val chatId: String,
    val userId: String,
    val username: String,
    val fullName: String? = null,
    val avatarUrl: String? = null,
    val overallRating: Double = 0.0,
    val position: String? = null,
    val club: String? = null,
    val country: String? = null,
    val cardImageUrl: String? = null,
    val cardAvatarUrl: String? = null,
    val status: String,
    val createdAt: String
)

@Serializable
data class MessagesPageDto(
    val messages: List<MessageDto>,
    val total: Long,
    val page: Int,
    val size: Int
)

// ── Request DTOs ──

@Serializable
data class CreateDirectChatRequest(
    val targetUserId: String
)

@Serializable
data class CreateGroupChatRequest(
    val name: String,
    val imageUrl: String? = null,
    val privacy: String? = "PUBLIC"
)

@Serializable
data class SendMessageRequest(
    val content: String,
    val type: String = "TEXT",
    val replyToMessageId: String? = null
)

@Serializable
data class InviteToChatRequest(
    val userIds: List<String>
)

@Serializable
data class UpdateGroupChatRequest(
    val name: String? = null,
    val imageUrl: String? = null,
    val privacy: String? = null
)

@Serializable
data class RespondToInvitationRequest(
    val accept: Boolean
)

@Serializable
data class RespondToJoinRequestRequest(
    val accept: Boolean
)

@Serializable
data class EditMessageRequest(
    val content: String
)

@Serializable
data class AddReactionRequest(
    val emoji: String
)

@Serializable
data class ForwardMessageRequest(
    val targetChatId: String
)

@Serializable
data class MuteChatRequest(
    val mute: Boolean
)

@Serializable
data class OnlineUsersResponse(
    val onlineUserIds: List<String>
)

// ── WebSocket event data ──

@Serializable
data class MessageDeletedData(
    val messageId: String,
    val chatId: String
)

@Serializable
data class MessageEditedData(
    val messageId: String,
    val chatId: String,
    val content: String
)

@Serializable
data class UserTypingData(
    val userId: String,
    val chatId: String,
    val username: String? = null
)

@Serializable
data class ChatMemberKickedData(
    val userId: String,
    val chatId: String
)

@Serializable
data class ReactionChangedData(
    val messageId: String,
    val chatId: String,
    val emoji: String,
    val userId: String
)

@Serializable
data class MessagePinnedData(
    val messageId: String,
    val chatId: String,
    val isPinned: Boolean
)

@Serializable
data class UserOnlineStatusData(
    val userId: String,
    val chatId: String,
    val isOnline: Boolean
)
